import React from 'react'
import { reportClientError } from 'utils/reportClientError'

interface Props {
  children: React.ReactNode
  /** Shown instead of the crashed subtree. Omit for the default message. */
  fallback?: React.ReactNode
}

interface State {
  error: Error | null
}

/**
 * Without this, a single uncaught error unmounts the entire application and
 * leaves a white page with nothing in it — no message, no trace, and nothing
 * for support to work from. That is exactly what happened on 2026-08-30: the
 * React root was mounted with no children at all, on every route.
 *
 * A boundary turns that into a visible failure and a reported one.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportClientError({
      kind: 'render',
      message: error?.message ?? String(error),
      stack: [error?.stack, info?.componentStack]
        .filter(Boolean)
        .join('\n')
        .split('\n')
        .slice(0, 10)
        .join('\n'),
    })
  }

  render(): React.ReactNode {
    const { error } = this.state
    const { children, fallback } = this.props
    if (!error) return children
    if (fallback) return fallback

    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 24,
          textAlign: 'center',
          color: '#fff',
          fontFamily: 'sans-serif',
        }}
      >
        <h2 style={{ margin: 0 }}>Something broke on this page</h2>
        <p style={{ margin: 0, opacity: 0.75, maxWidth: 520 }}>
          The problem has been reported automatically. Reloading usually helps; if it does not,
          the details below identify it.
        </p>
        <code style={{ opacity: 0.6, fontSize: 12, maxWidth: 640, wordBreak: 'break-word' }}>
          {error.message}
        </code>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: 8, padding: '8px 20px', cursor: 'pointer' }}
        >
          Reload
        </button>
      </div>
    )
  }
}
